import { agentTemplateOf, type VoiceAgentAudience } from "@wetalk/domain";
import { Tag } from "@wetalk/ui";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
import { useVoiceAgentAccess } from "../../api/slice/party-access.js";
import { partyAccessLockOf } from "./[id]/channels/access-lock.js";

/**
 * Who an agent_template's VoiceAgents talk to, read from the compiled catalogue's `audience` (plan-021 WAPP-PASS,
 * design §33.13.2, ED-2): the portal's internal tag and the pass's audience wall read this one value. A code the
 * catalogue does not hold reads `external` — the tag the portal drew for every template but `bi` before the catalogue
 * carried the property.
 *
 * AMENDED 2026-10-28 (plan-029 WAPP-ACCESS; design §40.25.2, D-12 as changed at AP-0): beside an internal VoiceAgent's
 * tag, the agent list draws the internal access lock — "Add who may call" while it will soon answer only the people on
 * its list, "Refuses every caller" once it does — with the full sentence as its title. CHANGED 2026-10-28 (plan-029
 * ACCESS-SPLIT): read from each internal VoiceAgent's access read (`lock_state`, shared with the agent page's query)
 * instead of the notice feed, so every reader sees it; only internal VoiceAgents read it.
 */
export function voiceAgentAudienceOf(agentTemplateCode: string): VoiceAgentAudience {
  return agentTemplateOf(agentTemplateCode)?.audience === "internal" ? "internal" : "external";
}

/** Intended audience in the portal; connection and conversation permissions are enforced separately. */
export function isInternalAgentTemplate(agentTemplateCode: string): boolean {
  return voiceAgentAudienceOf(agentTemplateCode) === "internal";
}

/** The audience tag — "Internal" or "External" — for an audience already known (a pass target, a candidate). */
export function AudienceTag({ audience }: { readonly audience: VoiceAgentAudience }) {
  usePortalTranslation("operations");
  const internal = audience === "internal";
  return <Tag tone={internal ? "accent" : "neutral"}>{portalText("operations", internal ? "agent_audience_internal" : "agent_audience_external")}</Tag>;
}

/**
 * The audience tag of a VoiceAgent's agent_template; with `voiceAgentId` (the agent list), an internal VoiceAgent also
 * carries its access lock tag.
 */
export function VoiceAgentAudienceTag({
  agentTemplateCode,
  voiceAgentId,
}: {
  readonly agentTemplateCode: string;
  readonly voiceAgentId?: string;
}) {
  const audience = voiceAgentAudienceOf(agentTemplateCode);
  return (
    <>
      <AudienceTag audience={audience} />
      {voiceAgentId === undefined || audience !== "internal" ? null : <InternalAccessLockTag voiceAgentId={voiceAgentId} />}
    </>
  );
}

/** The internal access lock as a tag (§40.25.2), or nothing when the access read names no lock for this VoiceAgent. */
export function InternalAccessLockTag({ voiceAgentId }: { readonly voiceAgentId: string }) {
  usePortalTranslation("callerProtection");
  const lock = partyAccessLockOf(useVoiceAgentAccess(voiceAgentId).data);
  if (lock === null) return null;
  return lock === "locked" ? (
    <span title={portalText("callerProtection", "access.locked")} data-access-lock="locked">
      <Tag status="critical">{portalText("callerProtection", "access.locked_tag")}</Tag>
    </span>
  ) : (
    <span title={portalText("callerProtection", "access.lock_pending")} data-access-lock="lock_pending">
      <Tag tone="accent-2">{portalText("callerProtection", "access.lock_pending_tag")}</Tag>
    </span>
  );
}
